import { AddressPartial } from '@domain';
import { ClientEntity } from 'src/app/domain/entities/client.entity';
import { CompanyEntity } from 'src/app/domain/entities/company.entity';
import { DocumentEntity } from 'src/app/domain/entities/document.entity';
import { DataTagContext } from './data-tag-context.token';

export function isFilled(value: unknown): boolean {
    if (value == null) return false;
    if (value instanceof Date) return !Number.isNaN(value.getTime());
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'number') return !Number.isNaN(value);
    if (typeof value === 'boolean') return value;
    return false;
}

function formatAddress(address?: AddressPartial): string {
    if (!address) return '';
    if (address.formatted?.trim()) return address.formatted.trim();
    const parts = [address.street, address.number, address.district, address.city, address.state]
        .map(p => (typeof p === 'string' ? p.trim() : ''))
        .filter(Boolean);
    if (parts.length === 0) return '';
    try {
        return AddressPartial.getFullAddress(address).trim();
    } catch {
        return parts.join(', ');
    }
}

function resolveAddressValue(entity?: { address?: AddressPartial }): string {
    return formatAddress(entity?.address);
}

function resolveCompanyField(company: CompanyEntity | undefined, field: string): unknown {
    if (!company) return undefined;
    switch (field) {
        case 'name':
            return company.name;
        case 'document':
            return company.document;
        case 'contact_name':
            return company.contactName;
        case 'email':
            return company.email;
        case 'phone':
            return company.phone;
        case 'address':
            return resolveAddressValue(company);
        default:
            return undefined;
    }
}

function resolveClientField(client: ClientEntity | undefined, field: string): unknown {
    if (!client) return undefined;
    switch (field) {
        case 'name':
            return client.name;
        case 'document':
            return client.document;
        case 'email':
            return client.email;
        case 'phone':
            return client.phone;
        case 'address':
            return resolveAddressValue(client);
        default:
            return undefined;
    }
}

/** Normaliza tag da API (ex.: COMPANY_NAME → link_company_name). */
function normalizeTagKey(tag: string): string {
    const key = tag.replace(/^\$/, '').trim();
    if (/^link_(document|company|client)_/.test(key)) return key;

    const legacy = /^(DOCUMENT|COMPANY|CLIENT)_(.+)$/i.exec(key);
    if (legacy) {
        return `link_${legacy[1].toLowerCase()}_${legacy[2].toLowerCase()}`;
    }

    return key;
}

/**
 * Retorna o valor bruto associado à tag no contexto da edição.
 * - Document: EditDataService.document (title, updatedAt)
 * - Company: CompanyController.currentCompany
 * - Client: EditDataService.client (ClientController.getByDocumentId no boot da edição)
 */
export function resolveDataTagValue(tag: string, ctx: DataTagContext | null | undefined): unknown {
    const key = normalizeTagKey(tag);

    const documentMatch = /^link_document_(.+)$/.exec(key);
    if (documentMatch) {
        const document = ctx?.document;
        if (!document) return undefined;
        if (documentMatch[1] === 'title') return document.title;
        if (documentMatch[1] === 'updated_at') return document.updatedAt;
        return undefined;
    }

    const companyMatch = /^link_company_(.+)$/.exec(key);
    if (companyMatch) {
        return resolveCompanyField(ctx?.company ?? undefined, companyMatch[1]);
    }

    const clientMatch = /^link_client_(.+)$/.exec(key);
    if (clientMatch) {
        return resolveClientField(ctx?.client ?? undefined, clientMatch[1]);
    }

    return undefined;
}

/**
 * Indica se a tag tem valor preenchido no contexto atual.
 * Sem contexto, assume preenchido (comportamento azul padrão).
 */
export function isDataTagFilled(tag: string, ctx: DataTagContext | null | undefined): boolean {
    if (!ctx) return true;
    return isFilled(resolveDataTagValue(tag, ctx));
}
